import CloseIcon from "@mui/icons-material/Close";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import api from "lib/api/api";

const STORAGE_KEY = "algermanist_cookie_consent_v1";

const text = {
  en: {
    title: "Cookie Settings",
    bannerTitle: "We use cookies",
    banner:
      "Necessary cookies keep the website working. Analytics and external media are optional and run only if you accept them.",
    necessary: "Necessary cookies",
    necessaryHelp: "Required for security, forms, bookings, and language settings.",
    analytics: "Analytics cookies",
    analyticsHelp: "Help us understand website usage and improve content.",
    external: "External services",
    externalHelp: "Allow services such as YouTube, Zoom, Stripe, and PayPal where used.",
    acceptAll: "Accept all",
    reject: "Necessary only",
    save: "Save settings",
    customize: "Customize",
    privacy: "Privacy Policy",
  },
  de: {
    title: "Cookie-Einstellungen",
    bannerTitle: "Wir verwenden Cookies",
    banner:
      "Notwendige Cookies halten die Webseite funktionsfähig. Analyse und externe Medien sind optional und laufen nur mit Ihrer Zustimmung.",
    necessary: "Notwendige Cookies",
    necessaryHelp: "Erforderlich für Sicherheit, Formulare, Buchungen und Spracheinstellungen.",
    analytics: "Analyse-Cookies",
    analyticsHelp: "Helfen uns zu verstehen, wie die Webseite genutzt wird.",
    external: "Externe Dienste",
    externalHelp: "Erlaubt Dienste wie YouTube, Zoom, Stripe und PayPal, soweit sie genutzt werden.",
    acceptAll: "Alle akzeptieren",
    reject: "Nur notwendige",
    save: "Einstellungen speichern",
    customize: "Anpassen",
    privacy: "Datenschutz",
  },
  ar: {
    title: "إعدادات ملفات الارتباط",
    bannerTitle: "نستخدم ملفات تعريف الارتباط",
    banner:
      "الملفات الضرورية تساعد الموقع على العمل. ملفات التحليل والخدمات الخارجية اختيارية ولا تعمل إلا بعد موافقتك.",
    necessary: "ملفات ضرورية",
    necessaryHelp: "مطلوبة للأمان والنماذج والحجوزات وإعدادات اللغة.",
    analytics: "ملفات التحليل",
    analyticsHelp: "تساعدنا على فهم استخدام الموقع وتحسين المحتوى.",
    external: "الخدمات الخارجية",
    externalHelp: "السماح بخدمات مثل YouTube وZoom وStripe وPayPal عند استخدامها.",
    acceptAll: "قبول الكل",
    reject: "الضرورية فقط",
    save: "حفظ الإعدادات",
    customize: "تخصيص",
    privacy: "سياسة الخصوصية",
  },
};

const getStoredConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const userId = useSelector((state) => state.auth.user?.userId);
  const accountCookieConsentAccepted = useSelector(
    (state) => state.auth.user?.cookieConsentAccepted
  );
  const language = i18n.language?.split("-")[0];
  const labels = text[language] || text.en;
  const dir = language === "ar" ? "rtl" : "ltr";
  const [bannerOpen, setBannerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    external: false,
  });

  const storedConsent = useMemo(() => getStoredConsent(), []);

  useEffect(() => {
    if (accountCookieConsentAccepted && !getStoredConsent()) {
      const accountConsent = {
        necessary: true,
        analytics: false,
        external: false,
        savedAt: new Date().toISOString(),
        version: 1,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(accountConsent));
      window.ALGERMANIST_COOKIE_CONSENT = accountConsent;
      setPreferences(accountConsent);
      setBannerOpen(false);
      return;
    }

    if (storedConsent) {
      setPreferences({
        necessary: true,
        analytics: Boolean(storedConsent.analytics),
        external: Boolean(storedConsent.external),
      });
      window.ALGERMANIST_COOKIE_CONSENT = storedConsent;
    } else {
      setBannerOpen(true);
    }
  }, [storedConsent, accountCookieConsentAccepted]);

  useEffect(() => {
    const openSettings = () => {
      const current = getStoredConsent();
      if (current) {
        setPreferences({
          necessary: true,
          analytics: Boolean(current.analytics),
          external: Boolean(current.external),
        });
      }
      setBannerOpen(false);
      setSettingsOpen(true);
    };
    window.addEventListener("openCookieSettings", openSettings);
    return () => window.removeEventListener("openCookieSettings", openSettings);
  }, []);

  const saveConsent = async (nextPreferences) => {
    const consent = {
      necessary: true,
      analytics: Boolean(nextPreferences.analytics),
      external: Boolean(nextPreferences.external),
      savedAt: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    window.ALGERMANIST_COOKIE_CONSENT = consent;
    window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: consent }));
    if (userId) {
      try {
        await api.put(`/users/${userId}/cookie-consent`, { accepted: true });
      } catch (error) {
        console.error("Failed to sync cookie consent", error);
      }
    }
    setPreferences(consent);
    setBannerOpen(false);
    setSettingsOpen(false);
  };

  const acceptAll = () => {
    void saveConsent({ necessary: true, analytics: true, external: true });
  };

  const rejectOptional = () => {
    void saveConsent({ necessary: true, analytics: false, external: false });
  };

  return (
    <>
      {bannerOpen && (
        <Paper
          elevation={8}
          role="dialog"
          aria-label={labels.title}
          sx={{
            position: "fixed",
            zIndex: 1500,
            left: { xs: 12, md: 24 },
            right: { xs: 12, md: 24 },
            bottom: { xs: 12, md: 24 },
            maxWidth: 980,
            mx: "auto",
            p: { xs: 2, md: 2.5 },
            borderRadius: 2,
            border: "1px solid #E4E7EC",
            direction: dir,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <CookieOutlinedIcon sx={{ color: "#B38B00", fontSize: 34 }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={850} sx={{ mb: 0.5 }}>
                {labels.bannerTitle}
              </Typography>
              <Typography sx={{ color: "#475467", lineHeight: 1.55 }}>
                {labels.banner}{" "}
                <Typography
                  component={Link}
                  to="/privacy-policy"
                  sx={{ color: "#111", fontWeight: 700, textDecoration: "underline" }}
                >
                  {labels.privacy}
                </Typography>
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ minWidth: { md: 430 } }}
            >
              <Button variant="outlined" onClick={rejectOptional} sx={{ borderRadius: 2 }}>
                {labels.reject}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setBannerOpen(false);
                  setSettingsOpen(true);
                }}
                sx={{ borderRadius: 2 }}
              >
                {labels.customize}
              </Button>
              <Button
                variant="contained"
                onClick={acceptAll}
                sx={{
                  bgcolor: "#F2C200",
                  color: "#111",
                  fontWeight: 800,
                  borderRadius: 2,
                  "&:hover": { bgcolor: "#E0B400" },
                }}
              >
                {labels.acceptAll}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, direction: dir } }}
      >
        <DialogTitle sx={{ pr: dir === "rtl" ? 3 : 6 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CookieOutlinedIcon sx={{ color: "#B38B00" }} />
            <Typography component="span" fontWeight={850}>
              {labels.title}
            </Typography>
          </Stack>
          <IconButton
            aria-label="Close"
            onClick={() => setSettingsOpen(false)}
            sx={{ position: "absolute", right: 12, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label={<Typography fontWeight={800}>{labels.necessary}</Typography>}
              />
              <Typography sx={{ color: "#667085", pl: dir === "rtl" ? 0 : 4, pr: dir === "rtl" ? 4 : 0 }}>
                {labels.necessaryHelp}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preferences.analytics}
                    onChange={(event) =>
                      setPreferences((current) => ({
                        ...current,
                        analytics: event.target.checked,
                      }))
                    }
                  />
                }
                label={<Typography fontWeight={800}>{labels.analytics}</Typography>}
              />
              <Typography sx={{ color: "#667085", pl: dir === "rtl" ? 0 : 4, pr: dir === "rtl" ? 4 : 0 }}>
                {labels.analyticsHelp}
              </Typography>
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={preferences.external}
                    onChange={(event) =>
                      setPreferences((current) => ({
                        ...current,
                        external: event.target.checked,
                      }))
                    }
                  />
                }
                label={<Typography fontWeight={800}>{labels.external}</Typography>}
              />
              <Typography sx={{ color: "#667085", pl: dir === "rtl" ? 0 : 4, pr: dir === "rtl" ? 4 : 0 }}>
                {labels.externalHelp}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
          <Button onClick={rejectOptional} variant="outlined" sx={{ borderRadius: 2 }}>
            {labels.reject}
          </Button>
          <Button
            onClick={() => void saveConsent(preferences)}
            variant="contained"
            sx={{
              bgcolor: "#F2C200",
              color: "#111",
              fontWeight: 800,
              borderRadius: 2,
              "&:hover": { bgcolor: "#E0B400" },
            }}
          >
            {labels.save}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
